import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  styled,
  CircularProgress,
  TextField,
} from '@mui/material';
import { X } from 'lucide-react';
import { apiService } from '../../../../api/apiService';
import useFunctionOptions from '../../utils/useFunctionOptions';
import ProcessMapUploadForm from '../../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import MultiFileUpload from '../../Forms/ProcessMapUploadForm/MutiFileUpload';
import SuccessPopup from '../../Popup/Success&ErrorPopup/SuccessPopup';
import ErrorPopup from '../../Popup/Success&ErrorPopup/ErrorPopup';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';


const StyledMenuItem = styled(MenuItem)({
  color: 'black',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
});

interface ProcessMapPopupProps {
  openUpload: boolean;
  setOpenUpload: Dispatch<SetStateAction<boolean>>;
  management: string;
  verticalTabValue?: string;
  onSuccess?: () => void;

}

const ProjectManagementPopup: React.FC<ProcessMapPopupProps> = ({
  openUpload,
  setOpenUpload,
  management,
  verticalTabValue,
  onSuccess
}) => {
  const { instance } = useMsal();
  const [selectedFiles, setSelectedFiles] = useState<Array<File | null>>([]);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [functionL1, setFunctionL1] = useState('');
  const [projectName, setProjectName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { options: l1Options, loading: l1Loading } = useFunctionOptions('l1', '');

  const isSpecialCase =
    verticalTabValue === 'Risk Assessment' || verticalTabValue === 'Success Change Management Plan';

    const isSubmitDisabled = () => {
      // Check if functionL1 is selected
      if (!functionL1) return true;
      
      // For special cases (Risk Assessment or Success Change Management Plan)
      if (isSpecialCase) {
        return !selectedFiles[0]; // Only check first file
      }
      
      // For other cases:
      // Check if projectName is filled and all 3 files are selected
      if (!projectName) return true;
      return !(selectedFiles[0] && selectedFiles[1] && selectedFiles[2]);
    };

  const handleFileSelect = (index: number, file: File | null) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[index] = file;
    setSelectedFiles(updatedFiles);

    const updatedProgress = [...uploadProgress];
    updatedProgress[index] = 0;
    setUploadProgress(updatedProgress);
  };

  const handleFileRemove = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles[index] = null;
    setSelectedFiles(updatedFiles);

    const updatedProgress = [...uploadProgress];
    updatedProgress[index] = 0;
    setUploadProgress(updatedProgress);
  };



  const handleSubmit = async () => {
    if (isSpecialCase ? selectedFiles[0] : selectedFiles.every((file) => file !== null)) {
      const formData = new FormData();
  
      const isChangeManagement = verticalTabValue === 'Risk Assessment' || verticalTabValue === 'Success Change Management Plan';
  
      const requestBody = {
        section_name: verticalTabValue,
        management: management,
        function_l1: functionL1,
        ...(!isChangeManagement && { current_project: projectName }), // Only include current_project if not a change management tab
        // current_project: projectName,

      };
  
      formData.append(isChangeManagement ? 'change_mgmt' : 'project_mgmt_req', JSON.stringify(requestBody));
  
      if (isSpecialCase) {
        formData.append(isChangeManagement ? 'project_change_mgmt_file' : 'file', selectedFiles[0] as File);
      } else {
        formData.append('project_charter_plan', selectedFiles[0] as File);
        formData.append('risks_and_propose', selectedFiles[1] as File);
        formData.append('kpi_financial_benefit', selectedFiles[2] as File);
      }
  
      setUploading(true);
  
      try {
        const endpoint = isChangeManagement ? '/management/project_change_management' : '/management/project_management';
        const response = await apiService(endpoint, 'POST', formData, undefined, instance as PublicClientApplication);
  
        if (response.error) {
          console.error('Error submitting project management request:', response.error);
          setErrorMessage(response.error);
          setShowError(true);
          return;
        }
  
        console.log('Submission successful:', response);
        setOpenUpload(false);
        resetUploadState();
        setShowSuccess(true);
        if (onSuccess) {
          onSuccess(); // Call the callback function after successful upload
        }
      } catch (error) {
        console.error('Error during submission:', error);
        setErrorMessage('An error occurred during submission.');
        setShowError(true);
      } finally {
        setUploading(false);
      }
    } else {
      console.error('Please upload all required files before submitting.');
      setErrorMessage('Please upload all required files before submitting.');
      setShowError(true);
    }
  };

  const resetUploadState = () => {
    setSelectedFiles([]);
    setUploadProgress([]);
    setFunctionL1('');
    setProjectName('');
  };


  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showError) {
      timer = setTimeout(() => {
        setShowError(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showError]);
  return (
    <>
      <Dialog
        open={openUpload}
        onClose={() => {
          setOpenUpload(false);
          resetUploadState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000' }}>
          {verticalTabValue}
          <IconButton onClick={() => setOpenUpload(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Function L1 *</InputLabel>
              <Select
                sx={{ borderRadius: '8px', color: '#000' }}
                value={functionL1}
                label="Function L1 *"
                onChange={(e) => setFunctionL1(e.target.value)}
              >
                <StyledMenuItem value="">Select Function L1</StyledMenuItem>
                {l1Loading ? (
                  <StyledMenuItem value="">
                    <CircularProgress size={24} />
                  </StyledMenuItem>
                ) : (
                  l1Options.map((option, index) => (
                    <StyledMenuItem key={index} value={option}>
                      {option}
                    </StyledMenuItem>
                  ))
                )}
              </Select>
            </FormControl>


            {isSpecialCase ? (
              null
            ) : (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <TextField
                  label="Name a current project under this function"
                  variant="outlined"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  sx={{ borderRadius: '8px', color: '#000' }}
                  InputLabelProps={{
                    style: { color: '#000' },
                  }}
                  InputProps={{
                    style: { color: '#000' },
                  }}
                />
              </FormControl>
            )}

            {isSpecialCase ? (
              <ProcessMapUploadForm
                file={selectedFiles[0]}
                onFileSelect={(file) => handleFileSelect(0, file)}
                fileTitle={
                  verticalTabValue === 'Risk Assessment'
                    ? 'For the projects in the center upload the PROSCI risk assessment matrix to define which projects need change management'
                    : 'Upload the Successful change management plan which involves strong adherence to communication plans, no reputational risks, employee turnover remained below 5%, and business KPIs had no disruptions.'
                }
                onFileRemove={() => handleFileRemove(0)}
              />
            ) : (
              <MultiFileUpload
                fileSlots={[0, 1, 2]}
                files={selectedFiles}
                uploadProgress={uploadProgress}
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setOpenUpload(false)} sx={{ color: '#000', borderColor: '#000' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled() || uploading}
            sx={{
              color: 'white',
              bgcolor: 'black',
              '&:hover': { bgcolor: 'black' },
              '&:disabled': { bgcolor: 'grey.300' },
            }}
          >
            {uploading ? 'Uploading...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Popup */}
      <SuccessPopup
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        message={`${verticalTabValue} uploaded successfully`}
      />

      {/* Error Popup */}
      <ErrorPopup
        open={showError}
        onClose={() => setShowError(false)}
        message={errorMessage}
      />

    </>
  );
};

export default ProjectManagementPopup;